<template>
    <div v-bind:class="showLabel ? 'requests-table' : 'bulk-table'">
        <label v-if="showLabel">Leistungen:</label>
        <div>
            <div class="bulk-status-filter" v-if="showStatusFilter">
                <div class="buttons has-addons">
                    <button
                        v-for="filter in statusFilters"
                        v-bind:key="filter.key"
                        class="button"
                        v-bind:class="{ 'is-primary': statusFilter === filter.key }"
                        v-on:click.prevent="statusFilter = filter.key"
                    >{{ filter.label }} ({{ filter.count }})</button>
                </div>

                <div class="buttons has-addons">
                    <button class="button" v-on:click.prevent="selectVisible" title="Alle aktuell sichtbaren Requests auswählen">Sichtbare auswählen</button>
                    <button class="button" v-on:click.prevent="clearSelection">Auswahl leeren</button>
                </div>
            </div>

            <edit-table
                ref="table"
                class="requests-edit-table is-mobile-scrollable"
                v-bind:rows="requests"
                v-bind:columns="columns"
                v-bind:load="false"
                v-bind:load-options="false"
                v-bind:delete-row="false"
                v-bind:footer="false"
                v-bind:hide-filter-row="true"
                v-bind:is-zebra="false"
                v-bind:name="{ singular: 'Leistung', plural: 'Leistungen' }"
            >
                <template v-slot:head>
                    <div class="row is-head">
                        <div class="cell is-icon-cell is-centered has-border" style="padding: 0">
                            <input type="checkbox" v-model="checkAll" />
                        </div>
                        <div class="cell is-pure-text has-border is-grow-cell">Leistung</div>
                        <div class="cell is-150px-cell is-pure-text has-border">Datum</div>
                        <div class="cell is-100px-cell is-pure-text has-border">Status</div>
                        <div class="cell is-100px-cell is-pure-text has-border">Details</div>
                        <div class="cell is-130px-cell is-pure-text has-border">Deadline</div>
                    </div>
                </template>

                <template v-slot:default>
                    <div v-if="visibleRequests.length === 0" class="row">
                        <div class="cell has-text-centered" style="flex-grow: 1">
                            <template v-if="statusFilter !== 'all'">Keine Leistungen mit diesem Status</template>
                            <template v-else>Keine Leistungen gefunden</template>
                        </div>
                    </div>

                    <template v-for="orderGroup in orderGroups">
                        <div
                            v-if="orderGroup.headline || (orderGroup.orderId && (groupBy === 'order' || orderGroups.length > 1))"
                            class="row is-headline not-white"
                            v-bind:key="'order-' + (orderGroup.headline || orderGroup.orderId)"
                        >
                            <div class="cell is-icon-cell is-centered" style="padding: 0"></div>
                            <div class="cell is-pure-text is-grow-cell" v-if="orderGroup.headline"><b>{{ orderGroup.headline }}</b></div>
                            <div class="cell is-pure-text is-grow-cell" v-else># {{ orderGroup.orderId }}</div>
                        </div>

                        <template v-for="group in orderGroup.groups">
                            <div class="row is-headline is-placeholder not-white" v-bind:key="group.key" v-if="group.label">
                                <div class="cell is-icon-cell is-centered has-border" style="padding: 0">
                                    <input
                                        type="checkbox"
                                        v-bind:checked="isGroupChecked(group)"
                                        v-on:change="checkGroup(group)"
                                        title="Alle Requests dieses Platzhalters auswählen"
                                    />
                                </div>
                                <div class="cell is-pure-text has-border is-grow-cell">
                                    <i v-if="typeIcons[group.requestType]" class="material-icons m-r" v-bind:title="typeIcons[group.requestType].title">{{ typeIcons[group.requestType].icon }}</i>
                                    <other-service-icons v-else class="m-r" v-bind:type="group.otherServiceCategory"></other-service-icons>
                                    <b class="is-truncated" v-bind:title="group.label">{{ group.label }}</b>
                                </div>
                                <div class="cell is-150px-cell is-pure-text has-border is-ellipsis">{{ dateRange(group.startAt, group.endAt) }}</div>
                                <div class="cell is-100px-cell has-border"></div>
                                <div class="cell is-100px-cell has-border"></div>
                                <div class="cell is-130px-cell has-border"></div>
                            </div>

                            <div class="row" v-for="request in group.requests" v-bind:key="request.id">
                                <div class="cell is-icon-cell is-centered has-border" style="padding: 0">
                                    <input type="checkbox" v-bind:checked="isChecked(request)" v-on:change="check(request)" />
                                </div>
                                <div class="cell is-pure-text has-border is-grow-cell" v-bind:title="request.info.title">
                                    <!-- im Platzhalter-Modus sitzt das Icon in der Platzhalter-Kopfzeile -->
                                    <template v-if="groupBy === 'order'">
                                        <i v-if="typeIcons[request.requestType]" class="material-icons m-r" v-bind:title="typeIcons[request.requestType].title">{{ typeIcons[request.requestType].icon }}</i>
                                        <other-service-icons v-else class="m-r" v-bind:type="request.otherServiceType.category"></other-service-icons>
                                    </template>
                                    <span class="is-truncated"><template v-if="groupBy === 'provider'"># {{ request.orderID }} {{ request.info.clientShortName }} – </template>{{ request.info.title }}</span>
                                </div>
                                <div class="cell is-150px-cell is-pure-text has-border is-ellipsis">{{ dateRange(request.info.startAt, request.info.endAt) }}</div>

                                <!-- Status -->
                                <div class="cell is-100px-cell is-pure-text has-border is-ellipsis">
                                    <template v-if="options">{{ getValueWithKey({ key: request.requestStatus.status, optionList: options.status }) }}</template>
                                </div>

                                <!-- Details -->
                                <div class="cell is-100px-cell is-pure-text has-border is-ellipsis">
                                    <template v-if="request.requestType === 'hotel' && options">{{ getValueWithKey({ key: request.board, optionList: options.boardList }) }}</template>
                                    <template v-else-if="request.requestType === 'ferry'">{{ request.type === 'night' ? 'Nachtfähre' : 'Tagfähre' }}</template>
                                </div>

                                <div class="cell is-130px-cell is-pure-text has-border is-ellipsis">
                                    <div v-if="request.optionTodo && request.optionTodo.dueDate" v-bind:class="['deadline-date', deadlineUrgency(request.optionTodo.dueDate)]">
                                        <i class="material-icons">notifications_none</i> {{ request.optionTodo.dueDate | fullYear }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </template>
                </template>
            </edit-table>
        </div>
    </div>
</template>



<script>
import uniqBy from 'lodash/uniqBy';
import OtherServiceIcons from '@components/OtherServiceIcons';
import EditTable from '@components/EditTable/Index.vue';
import { dayMonth, deadlineUrgency, fullYear, getValueWithKey, compareDates } from '@utilities/functions';


const TYPE_LABELS = {
    hotel:   'Hotel',
    ferry:   'Fähre',
    airline: 'Flug',
    train:   'Zug'
};

const TYPE_ICONS = {
    hotel:   { icon: 'home', title: 'Hotel' },
    ferry:   { icon: 'directions_boat', title: 'Fähre' },
    airline: { icon: 'airplanemode_active', title: 'Flug' },
    train:   { icon: 'directions_train', title: 'Zug' }
};


/*
 * Wiederverwendbare Leistungs-Auswahltabelle (EditTable-Optik) für das Email-Modal.
 * Selektion läuft über store.state.requestsSelected (ID-basiert); jede Änderung emittiert
 * 'change'. groupBy 'order' = klassische Ansicht (Email an Leistungsträger),
 * groupBy 'placeholder' = Massenemail-Ansicht mit Platzhalter-Kopfzeilen und Status-Filter.
 */
export default {
    props: {
        requests:         { type: Array, required: true },
        options:          {},
        groupBy:          { type: String, default: 'order' }, //'order' | 'placeholder' | 'provider'
        showStatusFilter: { type: Boolean, default: false },
        showLabel:        { type: Boolean, default: true } //false = volle Breite ohne linkes Label
    },


    data: function () {
        return {
            statusFilter: 'all',
            typeIcons: TYPE_ICONS,

            //nur für die EditTable-Pflicht-Prop; Kopf und Zeilen rendern eigene Slots
            columns: [
                { key: 'checkable', label: '', sortable: false },
                { key: 'title',     label: 'Leistung', sortable: false },
                { key: 'date',      label: 'Datum', sortable: false },
                { key: 'status',    label: 'Status', sortable: false },
                { key: 'details',   label: 'Details', sortable: false },
                { key: 'deadline',  label: 'Deadline', sortable: false }
            ]
        }
    },


    computed: {
        isLoadingProviders: function () {
            return this.$store.state.isLoadingProviders;
        },

        requestsSelected: {
            get: function () {
                return this.$store.state.requestsSelected;
            },
            set: function (value) {
                this.$store.state.requestsSelected = value;
            }
        },

        visibleRequests: function () {
            return this.requests.filter(request => this.isVisible(request));
        },

        //sichtbare Requests gruppiert nach Order (und je nach Modus nach Platzhalter)
        orderGroups: function () {
            //Listen-Modus (reiseübergreifend): nach Leistungsträger gebündelt, darin chronologisch
            if (this.groupBy === 'provider') {
                const providerOf = request => request.agency || request.provider || {};
                const keyOf = request => (request.agency ? 'agency-' : 'provider-') + providerOf(request).id;

                return uniqBy(this.visibleRequests, keyOf)
                    .map(request => ({
                        headline: providerOf(request).name || 'Ohne Leistungsträger',
                        groups: [{
                            key:      keyOf(request) + '-all',
                            label:    null,
                            requests: this.sortRequests(this.visibleRequests.filter(item => keyOf(item) === keyOf(request)))
                        }]
                    }))
                    .sort((a, b) => a.headline.localeCompare(b.headline));
            }

            return uniqBy(this.visibleRequests, 'orderID').map(request => request.orderID).map(orderId => {
                const requests = this.sortRequests(this.visibleRequests.filter(request => request.orderID === orderId));

                if (this.groupBy !== 'placeholder') {
                    return { orderId, groups: [{ key: orderId + '-all', label: null, requests }] };
                }

                const groupsMap = {};
                requests.forEach(request => {
                    const key = this.placeholderKey(request);
                    if (!groupsMap[key]) {
                        groupsMap[key] = {
                            key:         orderId + '-' + key,
                            label:       this.placeholderLabel(request),
                            requestType: request.requestType,
                            otherServiceCategory: request.otherServiceType ? request.otherServiceType.category : null,
                            startAt:     request.info.startAt,
                            endAt:       request.info.endAt,
                            requests:    []
                        };
                    }
                    groupsMap[key].requests.push(request);
                });

                const groups = Object.values(groupsMap).sort((a, b) => {
                    return compareDates(a.startAt, b.startAt) || (a.requests[0]._sort || 0) - (b.requests[0]._sort || 0);
                });

                return { orderId, groups };
            });
        },

        statusFilters: function () {
            const filters = [{ key: 'all', label: 'Alle', count: this.requests.length }];

            uniqBy(this.requests, request => request.requestStatus.status).forEach(request => {
                const status = request.requestStatus.status;
                filters.push({
                    key:   status,
                    label: this.options ? getValueWithKey({ key: status, optionList: this.options.status }) : status,
                    count: this.requests.filter(item => item.requestStatus.status === status).length
                });
            });

            return filters;
        },

        checkAll: {
            get: function () {
                return this.visibleRequests.length > 0 && this.visibleRequests.every(request => this.isChecked(request));
            },
            set: function (value) {
                if (value) {
                    this.addToSelection(this.visibleRequests);
                } else {
                    this.removeFromSelection(this.visibleRequests);
                }
            }
        }
    },


    methods: {
        sortRequests: function (requests) {
            return [...requests].sort((a, b) => {
                return compareDates(a.info.startAt, b.info.startAt) || (a._sort || 0) - (b._sort || 0);
            });
        },

        placeholderKey: function (request) {
            //placeholder kommt je nach Serializer-Gruppen als IRI-String oder als Objekt
            if (!!request.placeholder && typeof request.placeholder === 'string') {
                return request.requestType + '-' + request.placeholder.split('/').pop();
            }
            if (!!request.placeholder && request.placeholder.id) {
                return request.requestType + '-' + request.placeholder.id;
            }
            return request.requestType + '-request-' + request.id;
        },

        placeholderLabel: function (request) {
            if (request.placeholder && request.placeholder.serviceDescription) {
                return request.placeholder.serviceDescription;
            }
            let label = TYPE_LABELS[request.requestType];
            if (!label) {
                label = request.otherServiceType && request.otherServiceType.name ? request.otherServiceType.name : 'Leistung';
            }
            return label;
        },

        isVisible: function (request) {
            return this.statusFilter === 'all' || request.requestStatus.status === this.statusFilter;
        },

        isChecked: function (request) {
            return this.requestsSelected.findIndex(item => item.id === request.id) !== -1;
        },

        addToSelection: function (requests) {
            const selection = [...this.requestsSelected];
            requests.forEach(request => {
                if (selection.findIndex(item => item.id === request.id) === -1) {
                    selection.push(request);
                }
            });
            this.requestsSelected = selection;
            this.$emit('change');
        },

        removeFromSelection: function (requests) {
            this.requestsSelected = this.requestsSelected.filter(item => requests.findIndex(request => request.id === item.id) === -1);
            this.$emit('change');
        },

        check: function (request) {
            if (this.isChecked(request)) {
                this.removeFromSelection([request]);
            } else {
                this.addToSelection([request]);
            }
        },

        isGroupChecked: function (group) {
            return group.requests.length > 0 && group.requests.every(request => this.isChecked(request));
        },

        checkGroup: function (group) {
            if (this.isGroupChecked(group)) {
                this.removeFromSelection(group.requests);
            } else {
                this.addToSelection(group.requests);
            }
        },

        selectVisible: function () {
            this.addToSelection(this.visibleRequests);
        },

        clearSelection: function () {
            this.requestsSelected = [];
            this.$emit('change');
        },

        // eintägige Leistungen nur mit einem Datum, wie im Email-Template (serviceTableRow.twig)
        dateRange: function (startAt, endAt) {
            if (fullYear(startAt) === fullYear(endAt)) {
                return fullYear(startAt);
            }

            return dayMonth(startAt) + ' - ' + fullYear(endAt);
        },

        deadlineUrgency,
        getValueWithKey
    },


    watch: {
        //Lade-Spinner der EditTable ansteuern (rows kommen von außen aus dem Store)
        isLoadingProviders: function (value) {
            if (this.$refs.table) {
                this.$refs.table.isLoading = value;
            }
        }
    },


    mounted: function () {
        if (this.$refs.table) {
            this.$refs.table.isLoading = this.isLoadingProviders;
        }
    },


    filters: {
        dayMonth,
        fullYear
    },


    components: {
        OtherServiceIcons,
        EditTable
    }
}
</script>
